"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronDown } from "@untitledui/icons";
import type { ReactNode } from "react";
import { useState } from "react";
import { AnimatedInteger } from "@/components/base/animated-number/animated-number";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { capturePosthogEvent } from "@/lib/posthog-browser";
import { cx } from "@/utils/cx";
import type { Pool } from "../../data";
import type { PoolFilters } from "../../store/poolsStore";
import type { CoinPair } from "../../types";
import { downloadFile, exportToCsv, exportToJson, generateFilename } from "../../utils/export";
import { hasActivePoolFilters } from "../../utils/network-filter";
interface PoolsExportProps {
  pools: Pool[];
  filters: PoolFilters;
  selectedCoinPair: CoinPair | null;
  hasCsvDataDownloads: boolean;
  includeVolTvl?: boolean;
  isLoading?: boolean;
  toolbarActions?: ReactNode;
}
export function PoolsExport(t0) {
  const $ = _c(27);
  const {
    pools,
    filters,
    selectedCoinPair,
    hasCsvDataDownloads,
    includeVolTvl: t1,
    isLoading: t2,
    toolbarActions
  } = t0;
  const includeVolTvl = t1 === undefined ? false : t1;
  const isLoading = t2 === undefined ? false : t2;
  const [isOpen, setIsOpen] = useState(false);
  let t3;
  if ($[0] !== filters || $[1] !== hasCsvDataDownloads || $[2] !== includeVolTvl || $[3] !== pools || $[4] !== selectedCoinPair) {
    t3 = () => {
      if (!hasCsvDataDownloads) {
        return;
      }
      const csvContent = exportToCsv(pools, includeVolTvl);
      const filename = generateFilename(filters, selectedCoinPair, "csv");
      downloadFile(csvContent, filename, "text/csv");
      setIsOpen(false);
      capturePosthogEvent("pool_data_exported", {
        export_format: "csv",
        pool_count: pools.length,
        has_filters: hasActivePoolFilters(filters),
        coin_pair: selectedCoinPair?.id || null
      });
    };
    $[0] = filters;
    $[1] = hasCsvDataDownloads;
    $[2] = includeVolTvl;
    $[3] = pools;
    $[4] = selectedCoinPair;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleExportCsv = t3;
  let t4;
  if ($[6] !== filters || $[7] !== hasCsvDataDownloads || $[8] !== pools || $[9] !== selectedCoinPair) {
    t4 = () => {
      if (!hasCsvDataDownloads) {
        return;
      }
      const jsonContent = exportToJson(pools);
      const filename_0 = generateFilename(filters, selectedCoinPair, "json");
      downloadFile(jsonContent, filename_0, "application/json");
      setIsOpen(false);
      capturePosthogEvent("pool_data_exported", {
        export_format: "json",
        pool_count: pools.length,
        has_filters: hasActivePoolFilters(filters),
        coin_pair: selectedCoinPair?.id || null
      });
    };
    $[6] = filters;
    $[7] = hasCsvDataDownloads;
    $[8] = pools;
    $[9] = selectedCoinPair;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleExportJson = t4;
  let t5;
  if ($[11] !== isLoading || $[12] !== pools) {
    t5 = <div role="status" aria-live="polite" aria-atomic="true" className="text-sm font-semibold text-text-secondary tabular-nums">{isLoading ? <span className="inline-block h-4 w-20 animate-pulse rounded bg-secondary align-middle" aria-label="Loading results" /> : <><AnimatedInteger value={pools.length} /> {pools.length === 1 ? "result" : "results"}</>}</div>;
    $[11] = isLoading;
    $[12] = pools;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== handleExportCsv || $[15] !== handleExportJson || $[16] !== hasCsvDataDownloads || $[17] !== isLoading || $[18] !== isOpen || $[19] !== pools) {
    t6 = hasCsvDataDownloads ? <Dropdown.Root isOpen={isOpen} onOpenChange={setIsOpen}><Button color="secondary" size="sm" iconTrailing={ChevronDown} isDisabled={isLoading || pools.length === 0} className={cx(isOpen && "ring-2 ring-brand")}>Export</Button><Dropdown.Popover placement="bottom end"><Dropdown.Menu onAction={key => {
          if (key === "csv") {
            handleExportCsv();
          } else {
            if (key === "json") {
              handleExportJson();
            }
          }
        }}><Dropdown.Item id="csv" label="Export As CSV" /><Dropdown.Item id="json" label="Export As JSON" /></Dropdown.Menu></Dropdown.Popover></Dropdown.Root> : <Tooltip title="Export Data" description="This feature is available on the Max plan. Upgrade to export pool data as CSV or JSON files." placement="top"><span><Button color="secondary" size="sm" iconTrailing={ChevronDown} isDisabled={true} onClick={_temp}>Export</Button></span></Tooltip>;
    $[14] = handleExportCsv;
    $[15] = handleExportJson;
    $[16] = hasCsvDataDownloads;
    $[17] = isLoading;
    $[18] = isOpen;
    $[19] = pools;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t6 || $[22] !== toolbarActions) {
    t7 = <div className="flex items-center gap-2">{toolbarActions}{t6}</div>;
    $[21] = t6;
    $[22] = toolbarActions;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t5 || $[25] !== t7) {
    t8 = <div className="flex items-center justify-between border-b border-border-primary px-4 py-3 md:px-6">{t5}{t7}</div>;
    $[24] = t5;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
}
function _temp() {
  capturePosthogEvent("feature_access_denied", {
    feature: "csv_data_downloads",
    required_plan: "pro",
    context: "export_button"
  });
}